import { useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { onApiError } from "@/lib/api";

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
  userEmail?: string;
}

function getInitialSidebarState(): boolean {
  if (typeof document === "undefined") return true;
  const match = document.cookie.match(/(?:^|; )sidebar_state=([^;]*)/);
  return match ? match[1] !== "false" : true;
}

function ApiErrorBanner() {
  const [error, setError] = useState<string | null>(null);
  const lastShownAt = useRef(0);

  useEffect(() => {
    return onApiError((message) => {
      const now = Date.now();
      if (now - lastShownAt.current < 5000) return;
      lastShownAt.current = now;
      setError(message);
    });
  }, []);

  if (!error) return null;

  return (
    <div className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-foreground lg:mx-6">
      <AlertTriangle className="size-4 shrink-0 text-amber-600" />
      <span className="flex-1 truncate">
        API error: {error}
      </span>
      <button
        type="button"
        onClick={() => setError(null)}
        className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        aria-label="Dismiss"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

export default function Layout({ children, onLogout, userEmail }: LayoutProps) {
  return (
    <SidebarProvider
      defaultOpen={getInitialSidebarState()}
      style={{ "--sidebar-width": "calc(var(--spacing) * 68)" } as React.CSSProperties}
    >
      <AppSidebar
        variant="inset"
        collapsible="icon"
        onLogout={onLogout}
        userEmail={userEmail}
      />
      <SidebarInset
        className={cn(
          "peer-data-[variant=inset]:border",
          "[--dashboard-header-height:--spacing(12)]",
          "min-w-0 overflow-x-hidden",
        )}
      >
        <header
          className={cn(
            "flex h-12 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12",
          )}
        >
          <div className="flex w-full items-center justify-between px-4 lg:px-6">
            <div className="flex items-center gap-1 lg:gap-2">
              <SidebarTrigger className="-ml-1" />
              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4 data-[orientation=vertical]:self-center"
              />
            </div>
          </div>
        </header>
        <ApiErrorBanner />
        <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden">
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
